import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileLines,
  faCloudArrowUp,
  faScaleBalanced,
  faGears,
  faClipboardList,
  faLayerGroup,
  faMicrochip,
  faChevronDown,
  faPen,
  faTrashCan,
  faChartPie,
  faListCheck
} from '@fortawesome/free-solid-svg-icons';
import FeatherIcon from 'feather-icons-react';
import { useDispatch } from 'react-redux';
import { fetchCSAPlusProjectById, deleteCSAPlusProject } from './data-access/csa-plus.api';
import { useMainLayoutContext } from 'providers/MainLayoutProvider';
import { setToast } from 'components/Toasts/data-access/toast-reducer';
import PoliciesFrameworksTab from './components/PoliciesFrameworksTab';
import ConfigsControlsTab from './components/ConfigsControlsTab';
import LogsTab from './components/LogsTab';
import LogAnalysisTab from './components/LogAnalysisTab';
import CoverageAnalysisTab from './components/CoverageAnalysisTab';
import './ProjectDetails.css';

type NavItem = 'summary' | 'uploads' | 'policies-frameworks' | 'configs-controls' | 'logs' | 'dimensions' | 'cyber-ai';

const CSAPlusProjectDetails = () => {
  const { setContentClass } = useMainLayoutContext();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [project, setProject] = useState<{
    projectId?: string;
    title?: string;
    overview?: string;
    domain?: string;
    createdOn?: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeNavItem, setActiveNavItem] = useState<NavItem>('summary');
  const [uploadsExpanded, setUploadsExpanded] = useState(true);
  const [dimensionsExpanded, setDimensionsExpanded] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [cyberAiTab, setCyberAiTab] = useState<'coverage-analysis' | 'log-analysis'>('coverage-analysis');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDeleteClick = () => setShowDeleteConfirm(true);

  const handleDeleteCancel = () => setShowDeleteConfirm(false);

  const handleDeleteConfirm = async () => {
    if (!projectId) return;
    setIsDeleting(true);
    try {
      const userId = localStorage.getItem('userId') || undefined;
      await deleteCSAPlusProject(projectId, userId);
      setShowDeleteConfirm(false);
      dispatch(setToast({ show: true, title: 'Project deleted', message: 'The project has been deleted.' }));
      navigate('/csa+/all-projects');
    } catch (err: any) {
      dispatch(
        setToast({
          show: true,
          title: 'Delete failed',
          message: err?.response?.data?.message || err?.message || 'Could not delete project.'
        })
      );
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (!projectId) {
      setError('Project ID is missing');
      setIsLoading(false);
      return;
    }
    localStorage.setItem('projectId', projectId);

    const userId = localStorage.getItem('userId') || '';
    if (!userId) {
      setError('User not logged in');
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchCSAPlusProjectById(projectId, userId)
      .then(p => {
        if (cancelled || !p) return;
        setProject({
          projectId: p.projectId,
          title: p.title ?? '',
          overview: p.overview ?? '',
          domain: p.domain ?? '',
          createdOn: p.createdOn ?? ''
        });
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to fetch CSA+ project:', err);
        setError('Failed to load project');
        setProject(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    setContentClass('px-0 pt-navbar');
    return () => setContentClass('');
  }, [setContentClass]);

  const gradientStyle = {
    minHeight: '100vh',
    width: '100%',
    background: 'linear-gradient(293deg, rgb(239 243 255) 0%, rgb(255 255 255) 100%)',
    backgroundAttachment: 'fixed' as const,
    padding: '24px 16px',
    position: 'relative' as const
  };

  const isUploadsActive =
    activeNavItem === 'policies-frameworks' || activeNavItem === 'configs-controls' || activeNavItem === 'logs';

  if (isLoading) {
    return (
      <div
        style={{
          ...gradientStyle,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <CircularProgress size={50} />
        <p className="text-muted mt-2">Loading project...</p>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div style={gradientStyle}>
        <div className="text-center py-5">
          <p className="text-danger">{error || 'Project not found'}</p>
          <Link to="/csa+/all-projects" className="btn btn-outline-primary">
            Back to All Projects
          </Link>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeNavItem) {
      case 'summary':
        return (
          <div className="csa-content-card" style={{ padding: '24px 32px' }}>
            <h5 className="fw-bold mb-3">{project.title || 'Untitled Project'}</h5>
            {project.domain && (
              <div className="d-flex align-items-center gap-2 mb-2 text-body-secondary">
                <FeatherIcon icon="briefcase" size={16} />
                <span>{project.domain}</span>
              </div>
            )}
            {project.overview && <p className="text-body-secondary mb-2">{project.overview}</p>}
            {project.createdOn && <small className="text-muted">Created: {project.createdOn}</small>}
            {project.projectId && (
              <div className="mt-2">
                <small className="text-muted">Project ID: {project.projectId}</small>
              </div>
            )}
          </div>
        );
      case 'policies-frameworks':
        return <PoliciesFrameworksTab />;
      case 'configs-controls':
        return <ConfigsControlsTab />;
      case 'logs':
        return <LogsTab />;
      case 'dimensions':
      case 'cyber-ai':
        return (
          <div className="csa-content-card" style={{ padding: '24px 32px' }}>
            <div className="csa-cyber-ai-tabs">
              <div className="csa-cyber-ai-tabs-row">
                <button
                  type="button"
                  className={`csa-cyber-ai-tab ${cyberAiTab === 'coverage-analysis' ? 'active' : ''}`}
                  onClick={() => setCyberAiTab('coverage-analysis')}
                >
                  <FontAwesomeIcon icon={faChartPie} className="csa-cyber-ai-tab-icon" />
                  <span>Coverage Analysis</span>
                </button>
                <button
                  type="button"
                  className={`csa-cyber-ai-tab ${cyberAiTab === 'log-analysis' ? 'active' : ''}`}
                  onClick={() => setCyberAiTab('log-analysis')}
                >
                  <FontAwesomeIcon icon={faListCheck} className="csa-cyber-ai-tab-icon" />
                  <span>Log Analysis</span>
                </button>
              </div>
            </div>
            {/* Keep both mounted so Coverage / Log Analysis state survives tab switches */}
            <div
              className="csa-cyber-ai-tab-content"
              style={{ display: cyberAiTab === 'coverage-analysis' ? 'block' : 'none' }}
              aria-hidden={cyberAiTab !== 'coverage-analysis'}
            >
              <CoverageAnalysisTab />
            </div>
            <div
              className="csa-cyber-ai-tab-content"
              style={{ display: cyberAiTab === 'log-analysis' ? 'block' : 'none' }}
              aria-hidden={cyberAiTab !== 'log-analysis'}
            >
              <LogAnalysisTab isTabActive={cyberAiTab === 'log-analysis'} autoRunWhenEmpty hideProcessButton />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const sidebarWidth = 280;

  return (
    <div className="csa-project-details" style={gradientStyle}>
      <aside
        className="csa-sidebar"
        style={{
          position: 'fixed',
          left: 0,
          top: 'var(--phoenix-navbar-top-height, 72px)',
          bottom: 0,
          width: sidebarWidth,
          backgroundColor: '#fff',
          borderRight: '1px solid #e5e7eb',
          boxShadow: '2px 0 8px rgba(0,0,0,0.04)',
          zIndex: 90,
          overflowY: 'auto',
          padding: '20px 12px'
        }}
      >
        <nav className="d-flex flex-column gap-0">
          {/* Summary - first */}
          <div
            className={`csa-nav-item ${activeNavItem === 'summary' ? 'active' : ''}`}
            onClick={() => setActiveNavItem('summary')}
          >
            <div className="d-flex align-items-center gap-2">
              <FontAwesomeIcon
                icon={faFileLines}
                size="sm"
                className={activeNavItem === 'summary' ? 'text-primary' : 'text-muted'}
              />
              <span className="fw-semibold" style={{ fontSize: '14px' }}>
                Summary
              </span>
            </div>
          </div>

          {/* Uploads - expandable with submenu */}
          <div className="mt-2">
            <div
              className={`csa-nav-item ${isUploadsActive ? 'active' : ''}`}
              onClick={() => setUploadsExpanded(!uploadsExpanded)}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon
                    icon={faCloudArrowUp}
                    size="sm"
                    className={isUploadsActive ? 'text-primary' : 'text-muted'}
                  />
                  <span className="fw-semibold" style={{ fontSize: '14px' }}>
                    Uploads
                  </span>
                </div>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  size="xs"
                  className={`csa-nav-expandable text-muted ${
                    uploadsExpanded ? 'csa-nav-expandable-expanded' : 'csa-nav-expandable-collapsed'
                  }`}
                />
              </div>
            </div>

            <div
              className={`csa-nav-submenu mt-1 ${
                uploadsExpanded ? 'csa-nav-submenu-expanded' : 'csa-nav-submenu-collapsed'
              }`}
            >
              <div className="csa-nav-submenu-inner">
                <div className="ps-2 border-start border-2 ms-2" style={{ borderColor: '#e5e7eb' }}>
                  <div
                    className={`csa-nav-subitem ${activeNavItem === 'policies-frameworks' ? 'active' : ''}`}
                    onClick={() => setActiveNavItem('policies-frameworks')}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FontAwesomeIcon
                        icon={faScaleBalanced}
                        size="sm"
                        className={activeNavItem === 'policies-frameworks' ? 'text-primary' : 'text-muted'}
                      />
                      <span>Policies & Frameworks</span>
                    </div>
                  </div>
                  <div
                    className={`csa-nav-subitem ${activeNavItem === 'configs-controls' ? 'active' : ''}`}
                    onClick={() => setActiveNavItem('configs-controls')}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FontAwesomeIcon
                        icon={faGears}
                        size="sm"
                        className={activeNavItem === 'configs-controls' ? 'text-primary' : 'text-muted'}
                      />
                      <span>Configs & Controls</span>
                    </div>
                  </div>
                  <div
                    className={`csa-nav-subitem ${activeNavItem === 'logs' ? 'active' : ''}`}
                    onClick={() => setActiveNavItem('logs')}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FontAwesomeIcon
                        icon={faClipboardList}
                        size="sm"
                        className={activeNavItem === 'logs' ? 'text-primary' : 'text-muted'}
                      />
                      <span>Logs</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dimensions - expandable with Cyber-AI */}
          <div className="mt-2">
            <div
              className={`csa-nav-item ${
                activeNavItem === 'dimensions' || activeNavItem === 'cyber-ai' ? 'active' : ''
              }`}
              onClick={() => setDimensionsExpanded(!dimensionsExpanded)}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon
                    icon={faLayerGroup}
                    size="sm"
                    className={
                      activeNavItem === 'dimensions' || activeNavItem === 'cyber-ai' ? 'text-primary' : 'text-muted'
                    }
                  />
                  <span className="fw-semibold" style={{ fontSize: '14px' }}>
                    Dimensions
                  </span>
                </div>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  size="xs"
                  className={`csa-nav-expandable text-muted ${
                    dimensionsExpanded ? 'csa-nav-expandable-expanded' : 'csa-nav-expandable-collapsed'
                  }`}
                />
              </div>
            </div>

            <div
              className={`csa-nav-submenu mt-1 ${
                dimensionsExpanded ? 'csa-nav-submenu-expanded' : 'csa-nav-submenu-collapsed'
              }`}
            >
              <div className="csa-nav-submenu-inner">
                <div className="ps-2 border-start border-2 ms-2" style={{ borderColor: '#e5e7eb' }}>
                  <div
                    className={`csa-nav-subitem ${activeNavItem === 'cyber-ai' ? 'active' : ''}`}
                    onClick={() => setActiveNavItem('cyber-ai')}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FontAwesomeIcon
                        icon={faMicrochip}
                        size="sm"
                        className={activeNavItem === 'cyber-ai' ? 'text-primary' : 'text-muted'}
                      />
                      <span>Cyber-AI</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </aside>

      <main
        style={{
          marginLeft: sidebarWidth,
          minHeight: '100vh'
        }}
      >
        <div
          className="csa-content-card"
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
            margin: '24px 24px 24px 16px',
            overflow: 'hidden',
            transition: 'box-shadow 0.2s ease'
          }}
        >
          <div
            style={{
              padding: '32px 32px 24px 32px',
              borderBottom: '1px solid #e9ecef'
            }}
          >
            <div className="d-flex align-items-start justify-content-between">
              <h2 className="mb-0 fw-bold" style={{ fontSize: '28px', color: '#212529' }}>
                {project.title || 'Untitled Project'}
              </h2>
              <div className="d-flex align-items-center gap-3 csa-details-actions">
                <Link
                  to={`/csa+/update-project/${encodeURIComponent(projectId || '')}`}
                  className="csa-details-action csa-details-action-edit d-flex align-items-center gap-2"
                >
                  <FontAwesomeIcon icon={faPen} style={{ fontSize: '14px' }} />
                  Edit
                </Link>
                <button
                  type="button"
                  className="csa-details-action csa-details-action-delete d-flex align-items-center gap-2 border-0 bg-transparent"
                  onClick={handleDeleteClick}
                  disabled={isDeleting}
                >
                  <FontAwesomeIcon icon={faTrashCan} style={{ fontSize: '14px' }} />
                  {isDeleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
            {project.domain && (
              <div className="d-flex align-items-center gap-2 text-muted mt-2" style={{ fontSize: '14px' }}>
                <FeatherIcon icon="briefcase" size={16} />
                <span>{project.domain}</span>
              </div>
            )}
          </div>

          <div>{renderContent()}</div>
        </div>
      </main>

      {/* Delete confirmation dialog - toast-style dark theme */}
      {showDeleteConfirm && (
        <div className="csa-delete-confirm-overlay" onClick={handleDeleteCancel}>
          <div
            className="csa-delete-confirm-dialog"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="csa-delete-confirm-title"
          >
            <h5 id="csa-delete-confirm-title" className="csa-delete-confirm-title">
              Delete project?
            </h5>
            <p className="csa-delete-confirm-message">
              Are you sure you want to delete this project? This action cannot be undone.
            </p>
            <div className="csa-delete-confirm-actions">
              <button
                type="button"
                className="csa-delete-confirm-btn csa-delete-confirm-cancel"
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="csa-delete-confirm-btn csa-delete-confirm-delete"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CSAPlusProjectDetails;
